import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Container,
  LinearProgress,
} from '@mui/material';
import { CloudUpload, CheckCircle, Download } from '@mui/icons-material';
import { apiService, RevisionUploadResponse } from '../services/api';
import type { DocumentAssociation, ApprovalContext } from '../types';
import { getActionCompletionStatus, CompletionStatus } from '../services/planningExecutionApi';
import { useAuth } from '../contexts/AuthContext';
import { isInternalUser } from '../utils/sharePointLinkUtils';
import {
  dismissAfterActionForm,
  EXTERNAL_ACTION_FORM_COMPLETION_MESSAGE,
  shouldNavigateIntoPortfolioAfterAction,
} from '../utils/actionFormNavigation';
import { DocumentComments } from '../components/ProjectExecution/DocumentComments';
import { resolveDownloadFileName } from '../utils/downloadFileName';

const RevisionUploadPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<RevisionUploadResponse | null>(null);
  const [emailRequired, setEmailRequired] = useState(true);
  const [completionStatus, setCompletionStatus] = useState<CompletionStatus | null>(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [uploadDisabled, setUploadDisabled] = useState(false);
  const [actionDocument, setActionDocument] = useState<DocumentAssociation | null>(null);
  const [revisionContext, setRevisionContext] = useState<ApprovalContext | null>(null);

  // Allowed file extension for revision (must match source document type)
  const allowedExtension = actionDocument?.fileName
    ? (() => {
        const name = actionDocument.fileName;
        const lastDot = name.lastIndexOf('.');
        return lastDot === -1 ? '' : name.slice(lastDot).toLowerCase();
      })()
    : null;

  const getFileExtension = (filename: string) => {
    const lastDot = filename.lastIndexOf('.');
    return lastDot === -1 ? '' : filename.slice(lastDot).toLowerCase();
  };

  const fileTypeError =
    file && allowedExtension
      ? getFileExtension(file.name) !== allowedExtension
        ? `File type must match the source document (expected ${allowedExtension})`
        : null
      : null;

  const token = searchParams.get('token');
  const actionIdParam = searchParams.get('actionId');
  const projectIdParam = searchParams.get('projectId');
  const actionId = actionIdParam ? parseInt(actionIdParam, 10) : null;
  // @ts-ignore - intentionally unused
  const _projectId = projectIdParam ? parseInt(projectIdParam, 10) : null;

  // Allow access without token if actionId is provided (for logged-in users opening from execution view)
  useEffect(() => {
    if (!token && !actionId) {
      setError('Invalid revision link. No token or actionId provided.');
      setEmailRequired(false);
    } else if (!token && actionId) {
      // Opened from execution view - allow without token for logged-in users
      setEmailRequired(false); // Email not required when opened from execution view
      // Auto-fill email from logged-in user if available
      if (user?.email) {
        setEmail(user.email);
      }
    }
  }, [token, actionId, user]);

  // Check completion status and load action document (title/instructions) when actionId is available
  useEffect(() => {
    if (!actionId) {
      setCompletionStatus(null);
      setUploadDisabled(false);
      setActionDocument(null);
      setRevisionContext(null);
      return;
    }

    const checkStatus = async () => {
      setIsCheckingStatus(true);
      try {
        const status = await getActionCompletionStatus(actionId);
        setCompletionStatus(status);

        // If already completed, disable upload
        if (status.isCompleted) {
          setUploadDisabled(true);
        }

        // Load action documents for doc title and instructions (from source CREATE)
        try {
          const docs = await apiService.getActionDocuments(actionId);
          if (docs && docs.length > 0) {
            setActionDocument(docs[0]);
          }
        } catch (err) {
          console.error('Error loading action documents:', err);
        }

        // Load revision context (project, phase, task, document) for form display
        try {
          const ctx = await apiService.getRevisionContext(actionId);
          setRevisionContext(ctx);
        } catch (err) {
          console.error('Error loading revision context:', err);
        }
      } catch (error) {
        console.error('Error checking completion status:', error);
        // Don't set error state - this is optional
      } finally {
        setIsCheckingStatus(false);
      }
    };

    checkStatus();
  }, [actionId]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    // Email is required for token-based access, but optional for execution view access (logged-in users)
    if (emailRequired) {
      if (!email || email.trim() === '') {
        setError('Please enter your email address');
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setError('Please enter a valid email address');
        return;
      }
    } else {
      // For execution view access, use logged-in user's email if available
      if (!email && user?.email) {
        setEmail(user.email);
      }
    }

    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    if (fileTypeError) {
      setError(fileTypeError);
      return;
    }

    // Token is required for email-based access, but not for execution view access
    if (!token && !actionId) {
      setError('Invalid revision link. No token or actionId provided.');
      return;
    }

    setError(null);
    setLoading(true);
    setUploading(true);
    setUploadProgress(0);

    try {
      // Simulate upload progress (actual progress would come from the API)
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // Pass actionId if available from URL query parameter
      // Backend will use actionId directly if provided, otherwise falls back to token lookup
      // For execution view access (no token), pass null token and actionId
      // Ensure email is set (should be auto-filled from user, but double-check)
      const emailToUse = email || user?.email || '';
      if (!emailToUse) {
        setError('Email address is required. Please ensure you are logged in.');
        setLoading(false);
        setUploading(false);
        return;
      }

      const result = await apiService.uploadRevision(token || null, emailToUse, file, actionId);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // Handle alreadyCompleted response
      if (result.alreadyCompleted && result.completion) {
        // Update completion status
        setCompletionStatus({
          actionId: result.completion.actionId,
          isCompleted: true,
          completedBy: result.completion.completedBy,
          completedAt: result.completion.completedAt,
          details: result.completion.details,
        });
        setUploadDisabled(true);
      }
      
      setSuccess(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload revision');
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Revision Uploaded Successfully
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Thank you, {email}. Your revision has been uploaded and added as a new version in FRAmework.
          </Typography>
          {success.alreadyCompleted && success.completion && (
            <Alert severity="info" sx={{ mt: 2, mb: 3, textAlign: 'left' }}>
              <Typography variant="body2">
                <strong>Note:</strong> This action was already completed by{' '}
                {success.completion.completedBy.displayName}{' '}
                on {new Date(success.completion.completedAt).toLocaleDateString()}.
                Your file was uploaded successfully, but the action completion was recorded earlier.
              </Typography>
            </Alert>
          )}
          {actionDocument?.spItemId && (
            <Box sx={{ mb: 3, textAlign: 'left' }}>
              <DocumentComments
                file={{
                  spItemId: actionDocument.spItemId,
                  siteId: '',
                  driveId: '',
                  itemId: '',
                }}
              />
              <Button
                variant="outlined"
                size="small"
                sx={{ mt: 1 }}
                onClick={async () => {
                  try {
                    const nameOpts = {
                      fileName: actionDocument.fileName,
                      documentTitle: actionDocument.documentTitle,
                    };
                    if (isInternalUser(user)) {
                      const { blob, fileName } = await apiService.viewFileWithInfo(
                        actionDocument.spItemId!,
                        'SHAREPOINT'
                      );
                      apiService.triggerBlobDownload(
                        blob,
                        resolveDownloadFileName({
                          headerFileName: fileName,
                          blob,
                          ...nameOpts,
                        })
                      );
                    } else {
                      const { blob, fileName } = await apiService.downloadFileWithInfo(
                        actionDocument.spItemId!,
                        'SHAREPOINT'
                      );
                      apiService.triggerBlobDownload(
                        blob,
                        resolveDownloadFileName({
                          headerFileName: fileName,
                          blob,
                          ...nameOpts,
                        })
                      );
                    }
                  } catch (err) {
                    console.error('Failed to open document:', err);
                  }
                }}
              >
                {isInternalUser(user) ? 'View document' : 'Download document'}
              </Button>
            </Box>
          )}
          {!shouldNavigateIntoPortfolioAfterAction(user) ? (
            <Typography variant="body1" sx={{ mt: 2, fontWeight: 500 }}>
              {EXTERNAL_ACTION_FORM_COMPLETION_MESSAGE}
            </Typography>
          ) : (
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={() => {
                const pid = projectIdParam ? parseInt(projectIdParam, 10) : null;
                dismissAfterActionForm(navigate, user, {
                  internalProjectId: pid != null && !Number.isNaN(pid) ? pid : undefined,
                  internalFallbackPath: '/',
                });
              }}
            >
              {projectIdParam ? 'Return to Execution View' : 'Return to Home'}
            </Button>
          )}
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Upload Revision
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Select the revised file to upload.
          </Typography>
        </Box>

        {/* Completion Status Alert */}
        {completionStatus?.isCompleted && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Action Already Completed:</strong> This action was already completed by{' '}
              {completionStatus.completedBy?.displayName || completionStatus.completedBy?.email}{' '}
              on {completionStatus.completedAt ? new Date(completionStatus.completedAt).toLocaleDateString() : 'unknown date'}.
              {completionStatus.details?.fileName && (
                <> File: {completionStatus.details.fileName}</>
              )}
            </Typography>
          </Alert>
        )}

        {/* Loading completion status */}
        {isCheckingStatus && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Checking completion status...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {emailRequired && (
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Your Email Address"
              type="email"
              variant="outlined"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError(null);
              }}
              disabled={loading || (!token && !actionId)}
              sx={{ mb: 2 }}
            />

            <Box sx={{ mb: 2 }}>
              <input
                accept="*/*"
                style={{ display: 'none' }}
                id="revision-file-upload"
                type="file"
                onChange={handleFileSelect}
                disabled={loading || !token}
              />
              <label htmlFor="revision-file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                  startIcon={<CloudUpload />}
                  disabled={loading || !token}
                  sx={{ mb: 1 }}
                >
                  {file ? file.name : 'Select File'}
                </Button>
              </label>
              {file && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Selected: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                </Typography>
              )}
              {fileTypeError && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {fileTypeError}
                </Typography>
              )}
            </Box>

            {uploading && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Uploading... {uploadProgress}%
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            )}

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={loading || uploadDisabled || (!token && !actionId) || (emailRequired && !email) || !file || !!fileTypeError}
              startIcon={loading ? <CircularProgress size={20} /> : <CloudUpload />}
            >
              {loading ? 'Uploading...' : uploadDisabled ? 'Action Already Completed' : 'Upload Revision'}
            </Button>
          </Box>
        )}

        {!emailRequired && !token && actionId && (
          <Box sx={{ mb: 3 }}>
            {/* Revision context: project, phase, task, document (same pattern as approval form) */}
            {(revisionContext && (revisionContext.projectName || revisionContext.phaseName || revisionContext.phaseDescription || revisionContext.taskName || revisionContext.taskDescription || revisionContext.approvalActionName || revisionContext.sourceActionName || revisionContext.documentTitle || revisionContext.fileName)) && (
              <Paper
                variant="outlined"
                sx={{
                  mb: 3,
                  p: 2,
                  textAlign: 'left',
                  borderLeft: '4px solid',
                  borderColor: 'primary.main',
                  backgroundColor: 'primary.light',
                  '& .MuiTypography-root': { fontSize: '0.95rem' },
                }}
              >
                <Typography variant="subtitle1" fontWeight={600} color="primary.dark" gutterBottom>
                  Revision Request
                </Typography>
                {revisionContext.projectName && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Project:</strong> {revisionContext.projectName}
                  </Typography>
                )}
                {(revisionContext.phaseName || revisionContext.phaseDescription) && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Phase:</strong> {revisionContext.phaseName || ''}
                    {revisionContext.phaseDescription ? ` — ${revisionContext.phaseDescription}` : ''}
                  </Typography>
                )}
                {(revisionContext.taskName || revisionContext.taskDescription) && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Task:</strong> {revisionContext.taskName || ''}
                    {revisionContext.taskDescription ? ` — ${revisionContext.taskDescription}` : ''}
                  </Typography>
                )}
                {(revisionContext.approvalActionName || revisionContext.approvalActionDescription) && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Revision action:</strong> {revisionContext.approvalActionName || ''}
                    {revisionContext.approvalActionDescription ? ` — ${revisionContext.approvalActionDescription}` : ''}
                  </Typography>
                )}
                {(revisionContext.sourceActionName || revisionContext.sourceDocumentTitle || revisionContext.sourceDocumentDescription) && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Source action / document:</strong> {revisionContext.sourceActionName || ''}
                    {revisionContext.sourceDocumentTitle ? ` — ${revisionContext.sourceDocumentTitle}` : ''}
                    {revisionContext.sourceDocumentDescription ? ` / ${revisionContext.sourceDocumentDescription}` : ''}
                  </Typography>
                )}
                {(revisionContext.spItemId ?? actionDocument?.spItemId) != null && (
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={<Download />}
                    onClick={async () => {
                      const spItemId = revisionContext.spItemId ?? actionDocument?.spItemId!;
                      const nameOpts = {
                        fileName:
                          revisionContext.fileName ??
                          actionDocument?.fileName,
                        documentTitle:
                          revisionContext.documentTitle ??
                          actionDocument?.documentTitle,
                      };
                      try {
                        if (isInternalUser(user)) {
                          const { blob, fileName } = await apiService.viewFileWithInfo(
                            spItemId,
                            'SHAREPOINT'
                          );
                          apiService.triggerBlobDownload(
                            blob,
                            resolveDownloadFileName({
                              headerFileName: fileName,
                              blob,
                              ...nameOpts,
                            })
                          );
                        } else {
                          const { blob, fileName } = await apiService.downloadFileWithInfo(
                            spItemId,
                            'SHAREPOINT'
                          );
                          apiService.triggerBlobDownload(
                            blob,
                            resolveDownloadFileName({
                              headerFileName: fileName,
                              blob,
                              ...nameOpts,
                            })
                          );
                        }
                      } catch (err) {
                        console.error('Failed to open source document:', err);
                      }
                    }}
                    sx={{ mt: 1.5, mb: 1.5, py: 1.5, fontSize: '1rem' }}
                  >
                    {isInternalUser(user) ? 'View source document' : 'Download source document'}
                  </Button>
                )}
                {(revisionContext.documentTitle || revisionContext.documentDescription) && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Document:</strong> {revisionContext.documentTitle || ''}
                    {revisionContext.documentDescription ? (revisionContext.documentTitle ? ` / ${revisionContext.documentDescription}` : revisionContext.documentDescription) : ''}
                  </Typography>
                )}
                {revisionContext.fileName && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>File:</strong> {revisionContext.fileName}
                  </Typography>
                )}
              </Paper>
            )}
            {actionDocument?.spItemId && (
              <Box sx={{ mb: 2, textAlign: 'left' }}>
                <DocumentComments
                  file={{
                    spItemId: actionDocument.spItemId,
                    siteId: '',
                    driveId: '',
                    itemId: '',
                  }}
                />
              </Box>
            )}
            <Box sx={{ mb: 2 }}>
              <input
                accept="*/*"
                style={{ display: 'none' }}
                id="revision-file-upload-execution"
                type="file"
                onChange={handleFileSelect}
                disabled={loading}
              />
              <label htmlFor="revision-file-upload-execution">
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                  startIcon={<CloudUpload />}
                  disabled={loading}
                  sx={{ mb: 1 }}
                >
                  {file ? file.name : 'Select File'}
                </Button>
              </label>
              {file && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Selected: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                </Typography>
              )}
              {fileTypeError && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {fileTypeError}
                </Typography>
              )}
            </Box>

            {uploading && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Uploading... {uploadProgress}%
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            )}

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={loading || uploadDisabled || !file || !!fileTypeError}
              startIcon={loading ? <CircularProgress size={20} /> : <CloudUpload />}
            >
              {loading ? 'Uploading...' : uploadDisabled ? 'Action Already Completed' : 'Upload Revision'}
            </Button>
          </Box>
        )}

        {!token && !actionId && (
          <Alert severity="error">
            Invalid revision link. Please check the link and try again.
          </Alert>
        )}
      </Paper>
    </Container>
  );
};

export default RevisionUploadPage;

